// TODO: Consider using Zod schemas for cleaner parsing of API responses.
// The manual type guards below (parseSource, parseAnnotation, etc.) could be
// replaced with declarative schemas for better maintainability. See existing
// form schemas in src/components/modal/*/schemas.ts for patterns.

import type { ChatMessageResponse } from '@/types';
import { ChatMode as SchemaChatMode } from '@heidi/schema/ml-scribe';
import { logger } from '@/utils/logger/logger';
import type {
  Annotation,
  CalculatorResult,
  EvidenceImage,
  FormulaCalculationStep,
  FormulaCalculatorCategory,
  FormulaCalculatorResult,
  FormulaCriteriaBreakdown,
  SimpleCitation,
  Source,
} from '@heidi/evidence/contracts';
import { resolveMimsHtmlEmbed } from '@heidi/evidence/contracts';
import type { SessionByDateItem } from '@heidi/session/contracts';
import type { ClinicalTaskLink, ClinicalTaskResultTask } from '@heidi/tasks/contracts';
import type {
  CalculatorItemEntry,
  ClinicalTaskResultItemEntry,
  CsvExportItemEntry,
  ExcelExportItemEntry,
  FormulaCalculatorItemEntry,
  FormFillItemEntry,
  LibrarySearchItemEntry,
  PatientEvidencePdfItemEntry,
  PatientSummaryItemEntry,
  PdfExportItemEntry,
  PrintDocumentItemEntry,
  PptxExportItemEntry,
  ReasoningFragment,
  ReasoningItemEntry,
  SessionsByDateItemEntry,
  TextItemEntry,
  ThinkingItemEntry,
  TimelineEntry,
  UserMessageEntry,
  UnsupportedToolItemEntry,
  WebSearchItemEntry,
  WordExportItemEntry,
} from '../types/types';
import { ChatMode } from '../types/types';
import { QUOTA_UPGRADE_CTA_TEXT } from '../constants/quotaPaywall';

// ===== QUOTA PAYWALL DETECTION =====

function isQuotaExceededContent(text: string): boolean {
  return text.includes(QUOTA_UPGRADE_CTA_TEXT);
}

// ===== ID GENERATION =====

/**
 * Generates a deterministic ID for timeline entries.
 * Uses message_id when available, otherwise falls back to backendIndex.
 * This ensures the same backend data always produces the same IDs,
 * preventing duplicate entries on page reload (EVI-448).
 */
function generateDeterministicId(
  prefix: string,
  messageId: string | null | undefined,
  backendIndex: number,
  suffix?: string
): string {
  const base = messageId || `idx-${backendIndex}`;
  return suffix ? `${prefix}-${base}-${suffix}` : `${prefix}-${base}`;
}

// ===== MODE CONVERSION =====

function convertSchemaChatModeToLocalMode(
  schemaMode: SchemaChatMode | null | undefined
): ChatMode {
  if (!schemaMode) return ChatMode.EVIDENCE;

  const modeMap: Record<SchemaChatMode, ChatMode> = {
    [SchemaChatMode.evidence]: ChatMode.EVIDENCE,
    [SchemaChatMode.legacy]: ChatMode.LEGACY,
    [SchemaChatMode.auto]: ChatMode.AUTO, // Legacy: fast was RESEARCH, now maps to EVIDENCE v1
  };

  return modeMap[schemaMode] ?? ChatMode.EVIDENCE;
}

// ===== SOURCE PARSING =====

// Exported for the Evidence sub-agent citation normalizer — this single-source
// parser carries the same partner-branding rules `parseSources` does; reuse it
// rather than forking per-source mapping.
export function parseSource(
  source: unknown,
  messageId: string | null | undefined,
  backendIndex: number
): Source | null {
  if (typeof source === 'string') {
    return { type: 'url', url: source };
  }

  if (typeof source !== 'object' || source === null) {
    logger.warn('Unknown source format in chat message', {
      source,
      backendIndex,
      messageId,
    });
    return null;
  }

  const sourceObj = source as Record<string, unknown>;

  // Handle user_document type sources (library documents)
  if (sourceObj.type === 'user_document' || sourceObj.document_id) {
    return {
      type: 'user_document',
      url: (sourceObj.url as string) || '',
      title: sourceObj.title as string | undefined,
      document_id: sourceObj.document_id as string | undefined,
      snippet: sourceObj.snippet as string | undefined,
      // EVI-1592: backend now streams an array (`collections: string[]`) so a
      // document or domain can belong to multiple user collections. Tolerate
      // the legacy singular `collection_name` field for backward-compat with
      // any older persisted message payloads still in flight.
      collections: (sourceObj.collections as string[] | undefined) ??
        (sourceObj.collection_name ? [sourceObj.collection_name as string] : undefined),
    };
  }

  // EVI-590: Handle integration_partner type sources
  // Preserve partner metadata for frontend detection and branding
  if (sourceObj.type === 'integration_partner') {
    return {
      type: 'integration_partner',
      url: (sourceObj.url as string) || '',
      title: sourceObj.title as string | undefined,
      snippet: sourceObj.snippet as string | undefined,
      display_summary: sourceObj.display_summary as string | undefined,
      source_type: 'integration_partner',
      source_id: sourceObj.source_id as string | undefined,
      partner_type: sourceObj.partner_type as string | undefined,
      display_name: sourceObj.display_name as string | undefined,
      domain: sourceObj.domain as string | undefined,
      embed: resolveMimsHtmlEmbed(
        sourceObj.embed as Source['embed'],
        sourceObj.partner_type as string | undefined,
        sourceObj.url as string | undefined
      ),
      // EVI-1105: per-citation branding persisted on reload (flat snake_case,
      // sibling to partner_type). Without these, reloaded chats fall back to the
      // partner-level (neutral) logo instead of the zone-specific one.
      logo_url: sourceObj.logo_url as string | undefined,
      brand_color: sourceObj.brand_color as string | undefined,
      figures: sourceObj.figures as Source['figures'],
    };
  }

  // Handle URL-based sources (web search)
  if ('url' in sourceObj && typeof sourceObj.url === 'string') {
    const title = typeof sourceObj.title === 'string' ? sourceObj.title : undefined;
    return {
      type: 'url',
      url: sourceObj.url,
      ...(title && { title }),
    };
  }

  logger.warn('Unknown source format in chat message', {
    source,
    backendIndex,
    messageId,
  });
  return null;
}

// Exported for the Projects persisted-evidence builder (AGE-493): source
// parsing carries citation-branding rules (EVI-590 / EVI-1105) that must
// not be forked.
export function parseSources(
  sources: Array<Record<string, unknown>> | null | undefined,
  messageId: string | null | undefined,
  backendIndex: number
): Source[] {
  if (!sources) return [];

  return sources
    .map((source) => parseSource(source, messageId, backendIndex))
    // Accept sources with URL (web) OR document_id (library)
    .filter((source): source is Source => source !== null && (!!source.url || !!source.document_id));
}

// ===== ANNOTATION PARSING =====

function parseAnnotation(
  ann: unknown,
  messageId: string | null | undefined,
  backendIndex: number
): Annotation | null {
  if (
    typeof ann !== 'object' ||
    ann === null ||
    !('url' in ann) ||
    !('title' in ann) ||
    !('start_index' in ann) ||
    !('end_index' in ann)
  ) {
    logger.warn('Malformed annotation in chat message', {
      annotation: ann,
      backendIndex,
      messageId,
    });
    return null;
  }

  const { url, title, start_index, end_index } = ann as Record<string, unknown>;

  if (
    typeof url !== 'string' ||
    url.length === 0 ||
    typeof title !== 'string' ||
    !isValidIndex(start_index) ||
    !isValidIndex(end_index)
  ) {
    logger.warn('Malformed annotation in chat message', {
      annotation: ann,
      backendIndex,
      messageId,
    });
    return null;
  }

  return {
    type: 'url_citation',
    url,
    title,
    start_index: Number(start_index),
    end_index: Number(end_index),
  };
}

function isValidIndex(value: unknown): boolean {
  return (
    typeof value === 'number' || (typeof value === 'string' && !isNaN(Number(value)))
  );
}

function parseAnnotations(
  annotations: Array<Record<string, unknown>> | null | undefined,
  messageId: string | null | undefined,
  backendIndex: number
): Annotation[] {
  if (!annotations) return [];

  return annotations
    .map((ann) => parseAnnotation(ann, messageId, backendIndex))
    .filter((ann): ann is Annotation => ann !== null);
}

// ===== METADATA HELPERS =====

interface ClinicalTaskResult {
  id: string;
  status: 'completed' | 'failed';
  tasks: Array<{
    id: string;
    title: string;
    category: string | null;
    status: string;
    links?: ClinicalTaskLink[];
  }>;
  error?: string;
}

interface AdditionalMetadata {
  thinking_duration_ms?: number;
  // EVI-1567 / EV-023: persisted flag indicating the answer referenced a drug,
  // so the drug-interaction warning badge is restored on chat reload.
  drug_referenced?: boolean;
  evidence_chunks?: Array<{
    type?:
      | 'text'
      | 'web_search'
      | 'library_search'
      | 'medical_calculator'
      | 'calculator_result'
      | 'patient_summary';
    index?: number; // Position in stream for ordering
    // Text fields
    text?: string;
    citations?: SimpleCitation[];
    // Web/library search fields
    query?: string;
    sources?: Array<{ type: string; url: string; title?: string }>;
    images?: EvidenceImage[];
    // Medical calculator fields (when type: 'medical_calculator')
    calculator?: string; // Calculator ID (e.g., "wells_dvt", "bmi")
    result?: CalculatorResult; // Calculator result
    // Formula calculator fields (when type: 'calculator_result') - EVI-620
    formula_name?: string;
    formula_expression?: string;
    formula_display?: string;
    category?: FormulaCalculatorCategory;
    variables?: Record<string, number | string | boolean>;
    source_url?: string;
    source_name?: string;
    validation_reference?: string;
    criteria_breakdown?: FormulaCriteriaBreakdown[];
    calculation_steps?: FormulaCalculationStep[];
    warnings?: string[];
    // Patient summary fields (when type: 'patient_summary') - EVI-591
    patient_profile_id?: string;
  }>;
  // Legacy: Medical calculator results array (backward compatibility - EVI-544)
  // Prefer using evidence_chunks with type: 'medical_calculator' for correct ordering
  medical_calculator_results?: Array<{
    calculator: string; // Calculator ID (e.g., "bmi", "wells_dvt")
    result: CalculatorResult; // The calculator result
    index: number; // Position in original stream order
  }>;
  // PDF exports
  pdf_exports?: Array<{
    download_url: string;
    filename: string;
    title: string;
    index: number; // Position in original stream order
  }>;
  // print_document tool calls (web-only). Same shape as pdf_exports because
  // the backend renders the PDF through the same pipeline.
  print_documents?: Array<{
    download_url: string;
    filename: string;
    title: string;
    index: number;
  }>;
  // Word document exports
  word_exports?: Array<{
    download_url: string;
    filename: string;
    session_count?: number;
    index: number;
  }>;
  // CSV exports
  csv_exports?: Array<{
    download_url: string;
    filename: string;
    row_count?: number;
    index: number;
  }>;
  // Excel exports
  excel_exports?: Array<{
    download_url: string;
    filename: string;
    row_count?: number;
    index: number;
  }>;
  // PPTX exports
  pptx_exports?: Array<{
    download_url: string;
    preview_url?: string;
    filename: string;
    slide_count?: number;
    presentation_id?: string;
    index: number;
  }>;
  // Patient evidence PDF exports
  patient_evidence_pdf_exports?: Array<{
    download_url: string;
    filename: string;
    title: string;
    conditions_included?: number;
    index: number;
  }>;
  // Form fill exports (PDF forms filled via session context)
  form_fill_exports?: Array<{
    download_url?: string;
    filename?: string;
    title: string;
    fields_filled?: number;
    index: number;
    status?: 'filling' | 'completed' | 'error' | 'needs_template';
    error?: string;
  }>;
  // Sessions by date results
  sessions_by_date_results?: Array<{
    date: string;
    total_sessions: number;
    sessions: SessionByDateItem[];
    index: number;
  }>;
  clinical_task_results?: ClinicalTaskResult[];
  // Patient summary results (EVI-591: Pre-chart tool events)
  // Note: On reload, only completed results are stored (no status/error fields)
  patient_summary_results?: Array<{
    patient_profile_id: string;
    index: number; // Position in the response stream for ordering
  }>;
  // EHR FHIR tool call results (EVI-1408: persisted for reload + auditability)
  ehr_tool_calls?: Array<{
    tool: string; // e.g. "ehr_observation", "ehr_condition"
    action: string; // e.g. "search", "read"
    status: 'completed' | 'error';
    index: number;
    result_data?: Record<string, unknown>; // Parsed FHIR JSON for auditability
    error?: string; // Error message for failed calls
  }>;
}

/**
 * Extracts thinking duration from additional_metadata.
 * Used by both ReasoningItemEntry and WebSearchItemEntry.
 */
function extractThinkingDurationMs(additionalMetadata: unknown): number | undefined {
  return (additionalMetadata as AdditionalMetadata)?.thinking_duration_ms;
}

/**
 * Converts chat messages from API response format to timeline entries.
 * Handles user messages, assistant messages, and extracts metadata (reasoning, web search, annotations).
 */
export function convertChatMessagesToTimeline(
  messages: ChatMessageResponse[]
): TimelineEntry[] {
  const timeline: TimelineEntry[] = [];
  let position = 0;
  let backendIndex = 0;

  for (const message of messages) {
    if (message.role === 'user') {
      const userEntry: UserMessageEntry = {
        type: 'user_message',
        id: message.message_id || `user-${position}`,
        content: message.content,
        timestamp: message.timestamp ? new Date(message.timestamp).getTime() : Date.now(),
        position: position++,
        ...(message.attachments &&
          message.attachments.length > 0 && {
            files: message.attachments.map((attachment) => ({
              name: attachment.file_name,
              type: attachment.file_name.split('.').pop()?.toUpperCase() || 'UNKNOWN',
              file_key: attachment.file_key ?? undefined,
              url: attachment.url ?? undefined,
              attachment_id: attachment.attachment_id ?? undefined,
            })),
          }),
      };
      timeline.push(userEntry);
    } else if (message.role === 'assistant') {
      const metadata = message.metadata;
      const mode = convertSchemaChatModeToLocalMode(message.mode);
      const assistantEntriesStart = timeline.length;

      // Extract reasoning from metadata if present
      if (metadata?.reasoning) {
        const reasoningText = metadata.reasoning;
        // Split reasoning into fragments (simplified - could be enhanced)
        const reasoningFragments: ReasoningFragment[] = [[reasoningText, 0] as const];

        const reasoningEntry: ReasoningItemEntry = {
          type: 'reasoning_item',
          id: generateDeterministicId('reasoning', message.message_id, backendIndex),
          content: reasoningFragments,
          position: position++,
          backendIndex,
          mode,
          thinkingDurationMs: extractThinkingDurationMs(metadata?.additional_metadata),
        };
        timeline.push(reasoningEntry);
      }

      // EVI-334: Extract extended thinking from metadata if present
      if (metadata?.thinking) {
        const thinkingContent = metadata.thinking;
        const thinkingDuration = metadata.thinking_duration_ms;
        const thinkingEntry: ThinkingItemEntry = {
          type: 'thinking_item',
          id: generateDeterministicId('thinking', message.message_id, backendIndex),
          status: 'complete',
          thinking: thinkingContent,
          durationMs: thinkingDuration ?? undefined,
          position: position++,
          backendIndex,
          mode,
        };
        timeline.push(thinkingEntry);
      }

      // Check for EVIDENCE mode chunks in additional_metadata
      const additionalMeta = metadata?.additional_metadata as
        | AdditionalMetadata
        | undefined;
      const evidenceChunks = additionalMeta?.evidence_chunks;
      const legacyCalculatorResults = additionalMeta?.medical_calculator_results;
      // EVI-1567 / EV-023: restore the drug-interaction warning badge on reload.
      const drugReferenced = additionalMeta?.drug_referenced === true;

      // Check if evidence_chunks includes medical_calculator type (new format)
      const hasCalculatorsInChunks = evidenceChunks?.some(
        (c) => c.type === 'medical_calculator'
      );

      // Process legacy medical_calculator_results ONLY if not already in evidence_chunks
      // This maintains backward compatibility while preferring the new unified format
      if (!hasCalculatorsInChunks && legacyCalculatorResults?.length) {
        const sortedCalculators = [...legacyCalculatorResults].sort(
          (a, b) => a.index - b.index
        );
        sortedCalculators.forEach((calc) => {
          const calculatorEntry: CalculatorItemEntry = {
            type: 'calculator_item',
            id: generateDeterministicId(
              'calc',
              message.message_id,
              calc.index,
              `${calc.index}`
            ),
            toolId: `${message.message_id || backendIndex}-calc-${calc.index}`,
            calculator: calc.calculator,
            status: 'completed',
            result: calc.result,
            position: position++,
            backendIndex: calc.index,
            mode,
          };
          timeline.push(calculatorEntry);
        });
        const maxIndex = Math.max(...sortedCalculators.map((c) => c.index));
        backendIndex = maxIndex + 1;
      }

      if (mode === ChatMode.EVIDENCE && evidenceChunks?.length) {
        const groupId = message.message_id || `group-${backendIndex}`;

        // Normalize: For old format (no type field), inject web_search at beginning
        const isOldFormat = !evidenceChunks.some((c) => c.type);
        let normalizedChunks = evidenceChunks;

        if (isOldFormat && (metadata?.web_search_query || metadata?.sources)) {
          normalizedChunks = [
            {
              type: 'web_search' as const,
              query: metadata.web_search_query ?? undefined,
              sources: metadata.sources as (typeof evidenceChunks)[0]['sources'],
            },
            ...evidenceChunks.map((c) => ({ ...c, type: 'text' as const })),
          ];
        }

        // Sort chunks by index to preserve original stream order on page refresh
        // Chunks without index maintain their array position relative to indexed chunks
        const sortedChunks = [...normalizedChunks].sort((a, b) => {
          const aIndex = a.index ?? Infinity;
          const bIndex = b.index ?? Infinity;
          return aIndex - bIndex;
        });

        // Single loop for all chunks - handles text, web_search, and medical_calculator
        sortedChunks.forEach((chunk, idx) => {
          const chunkIndex = chunk.index ?? idx;

          if (chunk.type === 'web_search') {
            const sources = parseSources(
              chunk.sources as Array<Record<string, unknown>> | undefined,
              message.message_id,
              backendIndex
            );

            const searchEntry: WebSearchItemEntry = {
              type: 'web_search_item',
              id: generateDeterministicId(
                'web-search',
                message.message_id,
                backendIndex,
                `${chunkIndex}`
              ),
              query: chunk.query,
              sources,
              ...(chunk.images?.length && { images: chunk.images }),
              status: 'completed',
              position: position++,
              backendIndex: chunkIndex,
              mode,
              thinkingDurationMs: extractThinkingDurationMs(
                metadata?.additional_metadata
              ),
              // EVI-1486: stamp backend message_id so reloaded sessions
              // emit the same join key as live-stream sessions.
              ...(message.message_id ? { messageId: message.message_id } : {}),
            };
            timeline.push(searchEntry);
          } else if (chunk.type === 'library_search') {
            const sources = parseSources(
              chunk.sources as Array<Record<string, unknown>> | undefined,
              message.message_id,
              backendIndex
            );

            const librarySearchEntry: LibrarySearchItemEntry = {
              type: 'library_search_item',
              id: generateDeterministicId(
                'library-search',
                message.message_id,
                backendIndex,
                `${chunkIndex}`
              ),
              query: chunk.query,
              sources,
              status: 'completed',
              position: position++,
              backendIndex: chunkIndex,
              mode,
              ...(message.message_id ? { messageId: message.message_id } : {}),
            };
            timeline.push(librarySearchEntry);
          } else if (chunk.type === 'medical_calculator') {
            // Handle medical calculator chunks (new unified format)
            if (chunk.calculator && chunk.result) {
              const calculatorEntry: CalculatorItemEntry = {
                type: 'calculator_item',
                id: generateDeterministicId(
                  'calc',
                  message.message_id,
                  chunkIndex,
                  `${chunkIndex}`
                ),
                toolId: `${message.message_id || backendIndex}-calc-${chunkIndex}`,
                calculator: chunk.calculator,
                status: 'completed',
                result: chunk.result,
                position: position++,
                backendIndex: chunkIndex,
                mode,
              };
              timeline.push(calculatorEntry);
            }
          } else if (chunk.type === 'calculator_result') {
            // EVI-620: Handle formula calculator chunks (dynamic calculator)
            // Create entry if we have formula_name or result (either is sufficient)
            if (chunk.formula_name || chunk.result) {
              // Extract result fields - handle both nested and flat structures
              const resultData = chunk.result as FormulaCalculatorResult | undefined;
              const formulaResult: FormulaCalculatorResult | undefined = resultData
                ? {
                    value: resultData.value,
                    unit: resultData.unit,
                    interpretation: resultData.interpretation,
                    risk_level: resultData.risk_level,
                  }
                : undefined;

              const formulaCalculatorEntry: FormulaCalculatorItemEntry = {
                type: 'formula_calculator_item',
                id: generateDeterministicId(
                  'formula-calc',
                  message.message_id,
                  chunkIndex,
                  `${chunkIndex}`
                ),
                toolId: `${message.message_id || backendIndex}-formula-calc-${chunkIndex}`,
                formulaName: chunk.formula_name || 'Calculation',
                formulaExpression: chunk.formula_expression,
                formulaDisplay: chunk.formula_display,
                category: chunk.category || 'general_math',
                status: 'completed',
                variables: chunk.variables,
                result: formulaResult,
                sourceUrl: chunk.source_url,
                sourceName: chunk.source_name,
                validationReference: chunk.validation_reference,
                criteriaBreakdown: chunk.criteria_breakdown,
                calculationSteps: chunk.calculation_steps,
                warnings: chunk.warnings,
                position: position++,
                backendIndex: chunkIndex,
                mode,
              };
              timeline.push(formulaCalculatorEntry);
            }
          } else if (chunk.type === 'patient_summary') {
            // EVI-591: Handle patient summary chunks (pre-chart tool events)
            if (chunk.patient_profile_id) {
              const patientSummaryEntry: PatientSummaryItemEntry = {
                type: 'patient_summary_item',
                id: generateDeterministicId(
                  'patient-summary',
                  message.message_id,
                  chunkIndex,
                  `${chunkIndex}`
                ),
                toolId: `${message.message_id || backendIndex}-patient-summary-${chunkIndex}`,
                status: 'completed', // On reload, all stored results are completed
                patientProfileId: chunk.patient_profile_id,
                position: position++,
                backendIndex: chunkIndex,
                mode,
              };
              timeline.push(patientSummaryEntry);
            }
          } else {
            // EVI-590: Map 'type' to 'source_type' for integration partner detection
            // Backend sends type='integration_partner', frontend checks source_type
            const chunkCitations: SimpleCitation[] = (chunk.citations || []).map(
              (c) => ({
                ...c,
                source_type:
                  c.source_type ||
                  ((c as { type?: string }).type as SimpleCitation['source_type']),
              })
            );

            const chunkText = chunk.text || '';
            const textEntry: TextItemEntry = {
              type: 'text_item',
              id: `${message.message_id || backendIndex}-chunk-${chunkIndex}`,
              content: chunkText,
              annotations: [],
              citations: chunkCitations,
              groupId,
              position: position++,
              backendIndex: chunkIndex,
              mode,
              ...(isQuotaExceededContent(chunkText) && { quotaExceeded: 'detected' }),
              ...(drugReferenced && { drugReferenced: true }),
            };
            timeline.push(textEntry);
          }
        });

        backendIndex += sortedChunks.length;
      } else {
        // No evidence_chunks: Handle web_search from metadata if present
        if (metadata?.web_search_query || metadata?.sources) {
          const sources = parseSources(
            metadata.sources as Array<Record<string, unknown>> | undefined,
            message.message_id,
            backendIndex
          );

          const webSearchEntry: WebSearchItemEntry = {
            type: 'web_search_item',
            id: generateDeterministicId('web-search', message.message_id, backendIndex),
            query: metadata.web_search_query || undefined,
            sources,
            status: 'completed',
            position: position++,
            backendIndex,
            mode,
            thinkingDurationMs: extractThinkingDurationMs(metadata?.additional_metadata),
            // EVI-1486: stamp backend message_id (legacy/metadata reload path).
            ...(message.message_id ? { messageId: message.message_id } : {}),
          };
          timeline.push(webSearchEntry);
        }

        // Single TextItemEntry with annotations
        const annotations = parseAnnotations(
          metadata?.annotations as Array<Record<string, unknown>> | undefined,
          message.message_id,
          backendIndex
        );

        const textEntry: TextItemEntry = {
          type: 'text_item',
          id: message.message_id || `text-${backendIndex}`,
          content: message.content,
          annotations,
          position: position++,
          backendIndex,
          mode,
          ...(isQuotaExceededContent(message.content) && { quotaExceeded: 'detected' }),
          ...(drugReferenced && { drugReferenced: true }),
        };
        timeline.push(textEntry);

        backendIndex++;
      }

      // Handle clinical task results from additional_metadata (EVI-490)
      const clinicalTaskResults = (metadata?.additional_metadata as AdditionalMetadata)
        ?.clinical_task_results;
      if (clinicalTaskResults?.length) {
        for (const taskResult of clinicalTaskResults) {
          const clinicalTaskEntry: ClinicalTaskResultItemEntry = {
            type: 'clinical_task_result_item',
            id: generateDeterministicId(
              'clinical-task',
              message.message_id,
              backendIndex
            ),
            toolId: taskResult.id,
            status: taskResult.status,
            tasks: taskResult.tasks as ClinicalTaskResultTask[],
            error: taskResult.error,
            position: position++,
            backendIndex: backendIndex++,
            mode,
          };
          timeline.push(clinicalTaskEntry);
        }
      }

      // Handle patient summary results from additional_metadata (EVI-591)
      // Note: Only process if not already handled via evidence_chunks (similar to medical_calculator)
      const hasPatientSummaryInChunks = evidenceChunks?.some(
        (c) => c.type === 'patient_summary'
      );
      const patientSummaryResults = (metadata?.additional_metadata as AdditionalMetadata)
        ?.patient_summary_results;
      if (!hasPatientSummaryInChunks && patientSummaryResults?.length) {
        // Sort by index to preserve original stream order
        const sortedResults = [...patientSummaryResults].sort(
          (a, b) => a.index - b.index
        );
        for (const summaryResult of sortedResults) {
          const patientSummaryEntry: PatientSummaryItemEntry = {
            type: 'patient_summary_item',
            id: generateDeterministicId(
              'patient-summary',
              message.message_id,
              summaryResult.index
            ),
            toolId: `${message.message_id || backendIndex}-patient-summary-${summaryResult.index}`,
            status: 'completed', // On reload, all stored results are completed
            patientProfileId: summaryResult.patient_profile_id,
            position: position++,
            backendIndex: summaryResult.index,
            mode,
          };
          timeline.push(patientSummaryEntry);
        }
      }

      // Handle EHR FHIR tool call results from additional_metadata (EVI-1408)
      const ehrToolCalls = (metadata?.additional_metadata as AdditionalMetadata)
        ?.ehr_tool_calls;
      if (ehrToolCalls?.length) {
        const sortedEhrCalls = [...ehrToolCalls].sort((a, b) => a.index - b.index);
        for (const ehrCall of sortedEhrCalls) {
          const ehrEntry: UnsupportedToolItemEntry = {
            type: 'unsupported_tool_item',
            id: generateDeterministicId('ehr-tool', message.message_id, ehrCall.index),
            toolType: ehrCall.tool,
            status: ehrCall.status,
            rawData: ehrCall.action ? { action: ehrCall.action } : undefined,
            resultData: ehrCall.result_data,
            errorMessage: ehrCall.error,
            position: position++,
            backendIndex: ehrCall.index,
            mode,
          };
          timeline.push(ehrEntry);
        }
      }

      // Handle PDF exports from additional_metadata
      const pdfExports = (metadata?.additional_metadata as AdditionalMetadata)
        ?.pdf_exports;
      if (pdfExports?.length) {
        const sortedPdfExports = [...pdfExports].sort((a, b) => a.index - b.index);
        for (const pdfExport of sortedPdfExports) {
          const pdfExportEntry: PdfExportItemEntry = {
            type: 'pdf_export_item',
            id: generateDeterministicId('pdf-export', message.message_id, pdfExport.index),
            toolId: `${message.message_id || backendIndex}-pdf-export-${pdfExport.index}`,
            status: 'completed',
            title: pdfExport.title,
            downloadUrl: pdfExport.download_url,
            filename: pdfExport.filename,
            position: position++,
            backendIndex: pdfExport.index,
            mode,
          };
          timeline.push(pdfExportEntry);
        }
      }

      // Handle print_document tool calls from additional_metadata (web-only).
      // We intentionally do NOT re-fire the browser print dialog on reload —
      // that would be jarring. Instead we render the PrintDocumentBlock
      // with a Print button so the user can explicitly reprint.
      const printDocuments = (metadata?.additional_metadata as AdditionalMetadata)
        ?.print_documents;
      if (printDocuments?.length) {
        const sortedPrintDocs = [...printDocuments].sort((a, b) => a.index - b.index);
        for (const printDoc of sortedPrintDocs) {
          const printDocEntry: PrintDocumentItemEntry = {
            type: 'print_document_item',
            id: generateDeterministicId('print-document', message.message_id, printDoc.index),
            toolId: `${message.message_id || backendIndex}-print-document-${printDoc.index}`,
            status: 'completed',
            title: printDoc.title,
            downloadUrl: printDoc.download_url,
            filename: printDoc.filename,
            position: position++,
            backendIndex: printDoc.index,
            mode,
          };
          timeline.push(printDocEntry);
        }
      }

      // Handle patient evidence PDF exports from additional_metadata
      const patientEvidencePdfExports = (metadata?.additional_metadata as AdditionalMetadata)
        ?.patient_evidence_pdf_exports;
      if (patientEvidencePdfExports?.length) {
        const sortedExports = [...patientEvidencePdfExports].sort((a, b) => a.index - b.index);
        for (const pdfExport of sortedExports) {
          const pdfEntry: PatientEvidencePdfItemEntry = {
            type: 'patient_evidence_pdf_item',
            id: generateDeterministicId('patient-evidence-pdf', message.message_id, pdfExport.index),
            toolId: `${message.message_id || backendIndex}-patient-evidence-pdf-${pdfExport.index}`,
            status: 'completed',
            title: pdfExport.title,
            downloadUrl: pdfExport.download_url,
            filename: pdfExport.filename,
            conditionsIncluded: pdfExport.conditions_included,
            position: position++,
            backendIndex: pdfExport.index,
            mode,
          };
          timeline.push(pdfEntry);
        }
      }

      // Handle form fill exports from additional_metadata
      const formFillExports = (metadata?.additional_metadata as AdditionalMetadata)
        ?.form_fill_exports;
      if (formFillExports?.length) {
        const sortedFormFills = [...formFillExports].sort((a, b) => a.index - b.index);
        for (const formFill of sortedFormFills) {
          const formFillEntry: FormFillItemEntry = {
            type: 'form_fill_item',
            id: generateDeterministicId('form-fill', message.message_id, formFill.index),
            toolId: `${message.message_id || backendIndex}-form-fill-${formFill.index}`,
            status: formFill.status ?? 'completed',
            title: formFill.title,
            downloadUrl: formFill.download_url,
            filename: formFill.filename,
            fieldsFilled: formFill.fields_filled,
            error: formFill.error,
            position: position++,
            backendIndex: formFill.index,
            mode,
          };
          timeline.push(formFillEntry);
        }
      }

      // Handle Word exports from additional_metadata
      const wordExports = (metadata?.additional_metadata as AdditionalMetadata)
        ?.word_exports;
      if (wordExports?.length) {
        const sortedWordExports = [...wordExports].sort((a, b) => a.index - b.index);
        for (const wordExport of sortedWordExports) {
          const wordEntry: WordExportItemEntry = {
            type: 'word_export_item',
            id: generateDeterministicId('word-export', message.message_id, wordExport.index),
            toolId: `${message.message_id || backendIndex}-word-export-${wordExport.index}`,
            status: 'completed',
            filename: wordExport.filename,
            downloadUrl: wordExport.download_url,
            sessionCount: wordExport.session_count,
            position: position++,
            backendIndex: wordExport.index,
            mode,
          };
          timeline.push(wordEntry);
        }
      }

      // Handle CSV exports from additional_metadata
      const csvExports = (metadata?.additional_metadata as AdditionalMetadata)
        ?.csv_exports;
      if (csvExports?.length) {
        const sortedCsvExports = [...csvExports].sort((a, b) => a.index - b.index);
        for (const csvExport of sortedCsvExports) {
          const csvEntry: CsvExportItemEntry = {
            type: 'csv_export_item',
            id: generateDeterministicId('csv-export', message.message_id, csvExport.index),
            toolId: `${message.message_id || backendIndex}-csv-export-${csvExport.index}`,
            status: 'completed',
            filename: csvExport.filename,
            downloadUrl: csvExport.download_url,
            rowCount: csvExport.row_count,
            position: position++,
            backendIndex: csvExport.index,
            mode,
          };
          timeline.push(csvEntry);
        }
      }

      // Handle Excel exports from additional_metadata
      const excelExports = (metadata?.additional_metadata as AdditionalMetadata)
        ?.excel_exports;
      if (excelExports?.length) {
        const sortedExcelExports = [...excelExports].sort((a, b) => a.index - b.index);
        for (const excelExport of sortedExcelExports) {
          const excelEntry: ExcelExportItemEntry = {
            type: 'excel_export_item',
            id: generateDeterministicId('excel-export', message.message_id, excelExport.index),
            toolId: `${message.message_id || backendIndex}-excel-export-${excelExport.index}`,
            status: 'completed',
            filename: excelExport.filename,
            downloadUrl: excelExport.download_url,
            rowCount: excelExport.row_count,
            position: position++,
            backendIndex: excelExport.index,
            mode,
          };
          timeline.push(excelEntry);
        }
      }

      // Handle PPTX exports from additional_metadata
      const pptxExports = (metadata?.additional_metadata as AdditionalMetadata)
        ?.pptx_exports;
      if (pptxExports?.length) {
        const sortedPptxExports = [...pptxExports].sort((a, b) => a.index - b.index);
        for (const pptxExport of sortedPptxExports) {
          const pptxEntry: PptxExportItemEntry = {
            type: 'pptx_export_item',
            id: generateDeterministicId('pptx-export', message.message_id, pptxExport.index),
            toolId: `${message.message_id || backendIndex}-pptx-export-${pptxExport.index}`,
            status: 'completed',
            filename: pptxExport.filename,
            downloadUrl: pptxExport.download_url,
            previewUrl: pptxExport.preview_url,
            slideCount: pptxExport.slide_count,
            presentationId: pptxExport.presentation_id,
            position: position++,
            backendIndex: pptxExport.index,
            mode,
          };
          timeline.push(pptxEntry);
        }
      }

      // Handle sessions by date results from additional_metadata
      const sessionsByDateResults = (metadata?.additional_metadata as AdditionalMetadata)
        ?.sessions_by_date_results;
      if (sessionsByDateResults?.length) {
        const sortedResults = [...sessionsByDateResults].sort((a, b) => a.index - b.index);
        for (const dateResult of sortedResults) {
          const sessionsEntry: SessionsByDateItemEntry = {
            type: 'sessions_by_date_item',
            id: generateDeterministicId('sessions-by-date', message.message_id, dateResult.index),
            toolId: `${message.message_id || backendIndex}-sessions-by-date-${dateResult.index}`,
            status: 'completed',
            date: dateResult.date,
            totalSessions: dateResult.total_sessions,
            sessions: dateResult.sessions,
            position: position++,
            backendIndex: dateResult.index,
            mode,
          };
          timeline.push(sessionsEntry);
        }
      }

      // Re-sort this assistant message's entries by backendIndex so that
      // tool results (EHR, patient summary, etc.) interleave with text
      // chunks at the correct position — matching the original SSE order.
      const assistantEntries = timeline.slice(assistantEntriesStart);
      if (assistantEntries.length > 1) {
        const getIndex = (e: TimelineEntry): number =>
          'backendIndex' in e && typeof e.backendIndex === 'number' ? e.backendIndex : 0;
        assistantEntries.sort((a, b) => getIndex(a) - getIndex(b));
        for (let i = 0; i < assistantEntries.length; i++) {
          const entry = assistantEntries[i]!;
          entry.position = assistantEntriesStart + i;
          timeline[assistantEntriesStart + i] = entry;
        }
        position = assistantEntriesStart + assistantEntries.length;
      }
    }
  }

  return timeline;
}
